// Drives `--panel-width` on <html> via a class (`:root.panel*` in app.css) because inline styles trip CSP `style-src 'self'`.

export type PanelWidth = 0 | 320 | 420 | 480 | 640 | 820;

const CLASSES = ['panel320', 'panel420', 'panel480', 'panel640', 'panel820'] as const;

let currentWidth: PanelWidth = 0;

export function setPanelWidth(width: PanelWidth): void {
	const root = document.documentElement;
	root.classList.remove(...CLASSES);
	if (width !== 0) root.classList.add(`panel${width}`);
	currentWidth = width;
}

/** Last width applied by setPanelWidth. Reliable in handlers that run while the
 *  panel is already open (e.g. the attribute panel's "Zoom to" button); for code
 *  reacting to the selection change that *opens* the panel, use panelWidthFor
 *  instead — the syncing effect may not have run yet. */
export function getPanelWidth(): PanelWidth {
	return currentWidth;
}

/** Width the attributes panel occupies for the current viewport and app config
 *  (`attributes.panelWidth` caps the widest breakpoint). Pure function of its
 *  inputs, so callers reacting to a selection change don't depend on the
 *  `--panel-width` syncing effect having run first. */
export function panelWidthFor(hasSelection: boolean, appPanelWidth?: PanelWidth): PanelWidth {
	if (!hasSelection || typeof window === 'undefined') return 0;
	if (window.matchMedia('(min-width: 1100px)').matches) return appPanelWidth ?? 640;
	if (window.matchMedia('(min-width: 700px)').matches) return 420;
	return 320;
}

/** Fixed width of the AI-assistant side panel above the push breakpoint. The
 *  panel renders at min(420px, 90vw); this is the distance the map and
 *  left-anchored controls shift by (mirrored as --chat-push in app.css). */
const CHAT_PANEL_WIDTH = 420;

/** AI-panel push distance: CHAT_PANEL_WIDTH at/above 700px, 0 below (overlay). */
export function chatPushFor(): 0 | typeof CHAT_PANEL_WIDTH {
	if (typeof window === 'undefined') return 0;
	return window.matchMedia('(min-width: 700px)').matches ? CHAT_PANEL_WIDTH : 0;
}

/** Toggle the `chatOpen` class on <html> so --chat-push activates (class, not inline style, for CSP). */
export function setChatPanelOpen(open: boolean): void {
	document.documentElement.classList.toggle('chatOpen', open);
}
